/* <m-description-drawer> — the full product description behind a "show more" button.
 *
 * A simpler sibling of option-drawer.js. The one constraint it shares is the
 * important one: the drawer must be DISPLAYED at <body> level. Anywhere inside
 * the product section it paints under the header, and no z-index fixes it —
 * the cart drawer is rendered at body level in layout/theme.liquid for the same
 * reason, and the other bento drawers (search among them) move there on open.
 *
 * It does NOT share the variant-picker constraint. product-info-bento.js replaces
 * <variant-picker> on every option change, which is why an option drawer has to
 * come home before its radios are read; the description block is outside that
 * element and is never re-rendered mid-interaction, so this node can simply stay
 * at body level until it closes.
 */
if (!customElements.get("m-description-drawer")) {
  const openDrawers = new Set();
  const SCROLL_LOCK = "prevent-scroll";

  /* The scroll lock is shared with the cart drawer and the bento drawers
     (search among them). Only release it when nothing of ours is open AND no other overlay
     is holding it, otherwise closing this drawer would unlock the page behind an
     open cart drawer. */
  function releaseScrollLock() {
    if (openDrawers.size > 0) return;
    if (document.querySelector(".m-cart-drawer--active, .m-bento-drawer.is-open")) return;
    document.documentElement.classList.remove(SCROLL_LOCK);
  }

  /* textarea / select: the drawer also hosts the product page's "Need help?"
     contact form (sections/main-product-bento.liquid). */
  const FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

  class MDescriptionDrawer extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;

      this.wrapper = this.querySelector(".m-drawer__wrapper");
      this.panel = this.querySelector(".m-drawer__content");
      this.trigger = document.getElementById(this.dataset.triggerId);

      /* Where the node belongs when closed. Kept as a marker rather than a
         parent reference, because the parent's children shift while the drawer
         is away. */
      this.home = document.createComment("description-drawer");
      this.parentNode.insertBefore(this.home, this);

      this.onKeydown = this.onKeydown.bind(this);

      if (this.trigger) {
        this.trigger.addEventListener("click", () => this.open());
      }

      /* Any number of openers anywhere on the page (the search drawer has one
         in each header bar): `data-drawer-open="<id>"`. Delegated, so a
         re-rendered header keeps working. The one clicked gets focus back. */
      this.onDocumentClick = (event) => {
        const opener = event.target.closest?.('[data-drawer-open="' + this.id + '"]');
        if (!opener) return;
        event.preventDefault();
        this.trigger = opener;
        this.open();
      };
      if (this.id) document.addEventListener("click", this.onDocumentClick);

      /* Opening the cart closes any drawer: the cart is the one overlay that
         other scripts open on their own (add to cart). */
      window.LeonartoEvents?.subscribe?.(window.LeonartoTheme?.pubSubEvents?.openCartDrawer, () => this.close());

      this.querySelector("[data-description-drawer-close]")?.addEventListener("click", () => this.close());

      /* Clicking the backdrop closes. The panel stops the event so a click
         inside it never reaches the wrapper. */
      this.wrapper?.addEventListener("click", () => this.close());
      this.panel?.addEventListener("click", (event) => event.stopPropagation());

      /* A form inside the drawer posts and reloads the page; its result
         (success or errors) is rendered with this marker, so the drawer opens
         again to show it instead of leaving the answer behind a closed door.
         Shopify renders `posted_successfully?` / `errors` into every form of
         the page, not only the one that posted, so a marker carrying a form id
         opens only when the reload's hash (`/contact#<form id>`) names that
         form. Without a hash there is nothing to tell forms apart: open. */
      const marker = this.querySelector("[data-drawer-open-on-load]");
      if (marker) {
        const formId = marker.dataset.drawerOpenOnLoad;
        const hash = window.location.hash;
        if (!formId || !hash || hash === `#${formId}`) {
          requestAnimationFrame(() => this.open());
        }
      }
    }

    open() {
      if (this.isOpen) return;
      this.isOpen = true;

      /* Before the lock is taken: a listener may close another overlay (the
         header menu), and that one releases `prevent-scroll` as it goes. */
      this.dispatchEvent(new CustomEvent("bento-drawer:open"));

      document.body.appendChild(this);
      this.wrapper?.classList.remove("m:hidden");

      /* The class drives the transition, and it has to land in a later frame
         than the display change or there is nothing to transition from. */
      requestAnimationFrame(() => this.classList.add("is-open"));

      openDrawers.add(this);
      document.documentElement.classList.add(SCROLL_LOCK);
      this.trigger?.setAttribute("aria-expanded", "true");
      document.addEventListener("keydown", this.onKeydown);
      const focusTarget = this.dataset.drawerFocus && this.querySelector(this.dataset.drawerFocus);
      (focusTarget || this.panel)?.focus();
      this.dispatchEvent(new CustomEvent("bento-drawer:opened"));
    }

    close() {
      if (!this.isOpen) return;
      this.isOpen = false;

      this.classList.remove("is-open");
      openDrawers.delete(this);
      document.querySelectorAll('[data-drawer-open="' + this.id + '"]').forEach((el) => el.setAttribute("aria-expanded", "false"));
      document.removeEventListener("keydown", this.onKeydown);
      this.trigger?.setAttribute("aria-expanded", "false");
      this.trigger?.focus();

      const done = () => {
        /* Reopened before the slide ended: this close is stale. */
        if (this.isOpen) return;
        this.wrapper?.classList.add("m:hidden");
        /* Home again, so the Shopify editor and anything walking the section's
           DOM find the node where the markup put it. */
        this.home?.parentNode?.insertBefore(this, this.home);
        releaseScrollLock();
      };

      /* transitionend does not fire when the motion is suppressed, so the
         timeout is the one that runs under prefers-reduced-motion. */
      const settle = setTimeout(finish, 400);
      const panel = this.panel;
      /* Only the panel's own slide counts. transitionend bubbles: the close
         button's background fading after the click (and any other child
         transition) would otherwise end the close at once and cut the slide
         short. */
      function onEnd(event) {
        if (event.target !== panel) return;
        finish();
      }
      function finish() {
        clearTimeout(settle);
        panel?.removeEventListener("transitionend", onEnd);
        done();
      }
      panel?.addEventListener("transitionend", onEnd);
    }

    onKeydown(event) {
      if (event.key === "Escape") {
        event.stopPropagation();
        this.close();
        return;
      }

      if (event.key !== "Tab") return;

      /* Keep focus inside the dialog: at body level the rest of the page is
         still in the tab order. */
      const focusable = [...this.querySelectorAll(FOCUSABLE)].filter((el) => el.offsetParent !== null);
      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    disconnectedCallback() {
      /* Moving the node fires this too, so it runs twice per open/close cycle
         and not only when the drawer really leaves the page. Both movers are
         ordered around that: open() appends to <body> BEFORE it registers the
         drawer or takes the lock, and close() releases everything before it
         sends the node home. Reorder either and this callback tears down the
         state the same call just set up.

         Leaving the document with the lock held would strand the page. */
      openDrawers.delete(this);
      document.removeEventListener("keydown", this.onKeydown);
      releaseScrollLock();
    }
  }

  customElements.define("m-description-drawer", MDescriptionDrawer);
  /* The same drawer under a neutral name for drawers that are not a
     description (the search drawer, snippets/search-drawer-bento.liquid). */
  customElements.define("m-bento-drawer", class extends MDescriptionDrawer {});
}
